import { Injectable } from "@angular/core";
import { Observable, catchError, forkJoin, map, of, shareReplay } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AuthService } from "../auth/auth.service";
import { CompanyTrialStatusApiService } from "../company/company-trial-status-api.service";
import { CompanyApiService } from "../generated/waba-api/services/company-api.service";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";

/** Same first-run window as the default self-service trial. */
const NEW_CUSTOMER_ONBOARDING_DAYS = 14;

/**
 * Resolves the shell experience once and shares it between the automatic popup and the
 * header inbox. Trial dates cover self-service sign-ups; the company creation date also covers
 * brand-new accounts created manually by an administrator (which have no trial dates).
 *
 * This check intentionally fails closed: if the company cannot be read, product news stays
 * hidden. Showing no announcement during an API outage is safer than leaking an existing-client
 * announcement into a new customer's first session.
 */
@Injectable({ providedIn: "root" })
export class AnnouncementAudienceService {
  private readonly newCustomer$: Observable<boolean>;

  constructor(
    authService: AuthService,
    trialStatusService: CompanyTrialStatusApiService,
    companyApiService: CompanyApiService,
  ) {
    if (!authService.getCompanyId()) {
      this.newCustomer$ = of(false);
      return;
    }

    this.newCustomer$ = forkJoin({
      trial: trialStatusService
        .getTrialStatus()
        .pipe(catchError(() => of(undefined))),
      company: companyApiService
        .getCompanyByCompanyId(
          { "x-api-canal": "web", "x-api-requestId": uuidv4() },
          SILENT_HTTP_CONTEXT,
        )
        .pipe(
          map((response) => response.body),
          catchError(() => of(undefined)),
        ),
    }).pipe(
      map(({ trial, company }) => {
        // A trial remains a new-customer experience even after it expires, until the company
        // is explicitly converted and its trial dates are cleared server-side.
        if (trial?.endsAt || company?.trialEndsAt) {
          return true;
        }
        if (!company?.createdAt) {
          return true;
        }

        const createdAt = new Date(company.createdAt).getTime();
        if (!Number.isFinite(createdAt)) {
          return true;
        }
        const ageMs = Date.now() - createdAt;
        return (
          ageMs >= 0 &&
          ageMs <= NEW_CUSTOMER_ONBOARDING_DAYS * 24 * 60 * 60 * 1000
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  isNewCustomer(): Observable<boolean> {
    return this.newCustomer$;
  }
}
